/**
 * Pure helpers that turn the current URL path into (a) a human-readable label
 * and (b) the typed entity IDs, for the feedback-notes feature. No network —
 * names that need a lookup (PCO code, PDF filename) are passed in by the caller
 * from whatever query cache the page already has; a miss falls back to a short
 * id and never blocks the note.
 *
 * Built against the REAL app routes (App.tsx), not the build-spec's hypothetical
 * /projects/:projectId/... routes:
 *   /                              → PDF Inbox
 *   /inbox/:dailyPdfId             → PDF Inbox · {filename}
 *   /pcos                          → PCO Log
 *   /pcos/:pcoId                   → PCO Detail · PCO {code}
 *   /pcos/:pcoId/:weekEndingDate   → PCO Detail · PCO {code} · Rollup WE {M/D}
 *   /reconciliation                → Reconciliation
 *   /settings                      → Project Settings
 *   /admin/feedback                → Feedback (admin)
 */

export interface PageLabelLookup {
  /** PCO display number for /pcos/:pcoId routes, when the page has it cached. */
  pcoCode?: string | null;
  /** PDF filename for /inbox/:dailyPdfId, when cached. */
  pdfFilename?: string | null;
}

export interface PageContextIds {
  daily_pdf_id?: string;
  pco_id?: string;
  week_ending_date?: string;
}

function segments(pathname: string): string[] {
  const path = pathname.split('?')[0] ?? pathname;
  return path.split('/').filter(Boolean);
}

function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

/** 'YYYY-MM-DD' → 'M/D' (no leading zeros); passthrough if not a date. */
function weShort(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return ymd;
  return `${Number(m[2])}/${Number(m[3])}`;
}

export function getPageLabel(pathname: string, lookup: PageLabelLookup = {}): string {
  const seg = segments(pathname);
  if (seg.length === 0) return 'PDF Inbox';

  if (seg[0] === 'inbox') {
    const id = seg[1];
    if (!id) return 'PDF Inbox';
    return `PDF Inbox · ${lookup.pdfFilename || shortId(id)}`;
  }

  if (seg[0] === 'pcos') {
    const pcoId = seg[1];
    if (!pcoId) return 'PCO Log';
    const pcoLabel = lookup.pcoCode ? `PCO ${lookup.pcoCode}` : `PCO ${shortId(pcoId)}`;
    const week = seg[2];
    if (!week) return `PCO Detail · ${pcoLabel}`;
    return `PCO Detail · ${pcoLabel} · Rollup WE ${weShort(week)}`;
  }

  if (seg[0] === 'reconciliation') return 'Reconciliation';
  if (seg[0] === 'settings') return 'Project Settings';
  if (seg[0] === 'admin' && seg[1] === 'feedback') return 'Feedback (admin)';

  return `Unknown page (${pathname})`;
}

/**
 * Is this stored `page_route` safe to render as an href?
 *
 * `page_route` is captured client-side and persisted verbatim, so on the admin
 * review table it is user-submitted content — an authed PM could plant a
 * `javascript:` URL for the admin to click, or a protocol-relative `//host`
 * that leaves the app. Only a single-slash relative path qualifies; a second
 * `/` (protocol-relative) or a `\` (which URL parsing folds into `/`, making
 * `/\evil.test` cross-origin) disqualifies it. Everything else renders as
 * plain text. Every route this app actually produces passes.
 */
export function isSameOriginRoute(route: string | null | undefined): boolean {
  return typeof route === 'string' && /^\/(?![/\\])/.test(route);
}

export function getPageContextIds(pathname: string): PageContextIds {
  const seg = segments(pathname);
  const ctx: PageContextIds = {};

  if (seg[0] === 'inbox' && seg[1]) ctx.daily_pdf_id = seg[1];
  if (seg[0] === 'pcos' && seg[1]) {
    ctx.pco_id = seg[1];
    if (seg[2]) ctx.week_ending_date = seg[2];
  }

  return ctx;
}
